type SupabaseStorage = {
  getItem(key: string): string | null | Promise<string | null>
  setItem(key: string, value: string): void | Promise<void>
  removeItem(key: string): void | Promise<void>
}

type SupabaseSessionLike = {
  access_token?: string
  refresh_token?: string
  expires_at?: number
  currentSession?: SupabaseSessionLike
  session?: SupabaseSessionLike
}

type CookieRecord = {
  name: string
  value: string
}

const COOKIE_PARTS_SUFFIX = '.parts'
const DEFAULT_COOKIE_PATH = '/'
const DEFAULT_COOKIE_CHUNK_SIZE = 1000
const DEFAULT_FALLBACK_MAX_AGE_SECONDS = 7 * 24 * 60 * 60

function getCookiePartsName(storageKey: string): string {
  return `${storageKey}${COOKIE_PARTS_SUFFIX}`
}

function getCookieChunkName(storageKey: string, index: number): string {
  return `${storageKey}.${index}`
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function parseCookieString(cookieString: string): Map<string, string> {
  const cookies = new Map<string, string>()

  for (const entry of cookieString.split(';')) {
    const trimmed = entry.trim()
    if (!trimmed) {
      continue
    }

    const separatorIndex = trimmed.indexOf('=')
    if (separatorIndex === -1) {
      continue
    }

    const name = trimmed.slice(0, separatorIndex).trim()
    const value = trimmed.slice(separatorIndex + 1).trim()
    cookies.set(name, value)
  }

  return cookies
}

function readChunkCount(storageKey: string, cookies: Map<string, string>): number | null {
  const partsValue = cookies.get(getCookiePartsName(storageKey))
  if (!partsValue) {
    return cookies.has(storageKey) ? 1 : null
  }

  const chunkCount = Number.parseInt(partsValue, 10)
  if (!Number.isInteger(chunkCount) || chunkCount <= 0) {
    return null
  }

  return chunkCount
}

function collectCookieChunkNames(storageKey: string, cookieString: string): string[] {
  const cookies = parseCookieString(cookieString)
  const chunkCount = readChunkCount(storageKey, cookies)
  const names = new Set<string>()

  names.add(storageKey)
  names.add(getCookiePartsName(storageKey))

  if (!chunkCount) {
    return [...names]
  }

  for (let index = 0; index < chunkCount; index += 1) {
    names.add(getCookieChunkName(storageKey, index))
  }

  return [...names]
}

function setBrowserCookie(name: string, value: string, maxAge: number): void {
  const segments = [`${name}=${value}`, `Path=${DEFAULT_COOKIE_PATH}`, 'SameSite=Lax']

  if (Number.isFinite(maxAge)) {
    segments.push(`Max-Age=${Math.max(0, Math.floor(maxAge))}`)
  }

  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    segments.push('Secure')
  }

  document.cookie = segments.join('; ')
}

function removeBrowserCookie(name: string): void {
  const segments = [`${name}=`, `Path=${DEFAULT_COOKIE_PATH}`, 'SameSite=Lax', 'Max-Age=0']

  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    segments.push('Secure')
  }

  document.cookie = segments.join('; ')
}

function removeLegacyLocalStorageKey(storageKey: string): void {
  if (!isBrowser()) {
    return
  }

  try {
    window.localStorage.removeItem(storageKey)
  } catch {
    // noop: localStorage can be blocked in some browser contexts
  }
}

export function buildSupabaseAuthStorageKey(supabaseUrl: string): string {
  if (typeof supabaseUrl !== 'string' || supabaseUrl.trim() === '') {
    throw new Error('[Auth] VITE_SUPABASE_URL が設定されていません。')
  }

  let supabaseHost: string
  try {
    supabaseHost = new URL(supabaseUrl).hostname
  } catch (error) {
    throw new Error(`[Auth] VITE_SUPABASE_URL の解析に失敗しました: ${String(error)}`)
  }

  const projectRef = supabaseHost.split('.')[0]
  if (!projectRef) {
    throw new Error('[Auth] Supabase Project Ref の抽出に失敗しました。')
  }

  return `sb-${projectRef}-auth-token`
}

export function buildChunkedCookieRecords(
  storageKey: string,
  rawValue: string,
  chunkSize = DEFAULT_COOKIE_CHUNK_SIZE,
): CookieRecord[] {
  const chunks: CookieRecord[] = []

  for (let index = 0; index < rawValue.length; index += chunkSize) {
    const chunkValue = rawValue.slice(index, index + chunkSize)
    chunks.push({
      name: getCookieChunkName(storageKey, chunks.length),
      value: encodeURIComponent(chunkValue),
    })
  }

  if (chunks.length === 0) {
    chunks.push({
      name: getCookieChunkName(storageKey, 0),
      value: encodeURIComponent(rawValue),
    })
  }

  return [
    {
      name: getCookiePartsName(storageKey),
      value: String(chunks.length),
    },
    ...chunks,
  ]
}

export function readChunkedCookieValue(storageKey: string, cookieString: string): string | null {
  const cookies = parseCookieString(cookieString)
  const chunkCount = readChunkCount(storageKey, cookies)

  if (!chunkCount) {
    const singleValue = cookies.get(storageKey)
    return singleValue ? decodeURIComponent(singleValue) : null
  }

  const parts: string[] = []
  for (let index = 0; index < chunkCount; index += 1) {
    const encodedChunk = cookies.get(getCookieChunkName(storageKey, index))
    if (!encodedChunk) {
      return null
    }
    parts.push(decodeURIComponent(encodedChunk))
  }

  return parts.join('')
}

function parseSupabaseSession(rawValue: string): SupabaseSessionLike | null {
  try {
    const parsed = JSON.parse(rawValue) as SupabaseSessionLike | null
    if (!parsed || typeof parsed !== 'object') {
      return null
    }

    if (
      typeof parsed.access_token === 'string' ||
      typeof parsed.refresh_token === 'string' ||
      typeof parsed.expires_at === 'number'
    ) {
      return parsed
    }

    if (parsed.currentSession && typeof parsed.currentSession === 'object') {
      return parsed.currentSession
    }

    if (parsed.session && typeof parsed.session === 'object') {
      return parsed.session
    }

    return parsed
  } catch {
    return null
  }
}

export function extractAccessTokenFromSupabaseSession(rawValue: string): string | null {
  const session = parseSupabaseSession(rawValue)
  return typeof session?.access_token === 'string' && session.access_token.length > 0
    ? session.access_token
    : null
}

export function extractSupabaseSessionExpiresAt(rawValue: string): number | null {
  const session = parseSupabaseSession(rawValue)
  return typeof session?.expires_at === 'number' && Number.isFinite(session.expires_at)
    ? session.expires_at
    : null
}

export function resolveSupabaseSessionMaxAge(
  rawValue: string,
  nowSeconds = Math.floor(Date.now() / 1000),
  fallbackSeconds = DEFAULT_FALLBACK_MAX_AGE_SECONDS,
): number {
  const expiresAt = extractSupabaseSessionExpiresAt(rawValue)
  if (expiresAt === null) {
    return fallbackSeconds
  }

  return Math.max(1, expiresAt - nowSeconds)
}

function migrateLegacyLocalStorageValue(storageKey: string): string | null {
  if (!isBrowser()) {
    return null
  }

  let legacyValue: string | null = null
  try {
    legacyValue = window.localStorage.getItem(storageKey)
  } catch {
    return null
  }

  if (!legacyValue || !extractAccessTokenFromSupabaseSession(legacyValue)) {
    return null
  }

  const maxAge = resolveSupabaseSessionMaxAge(legacyValue)
  for (const { name, value } of buildChunkedCookieRecords(storageKey, legacyValue)) {
    setBrowserCookie(name, value, maxAge)
  }

  removeLegacyLocalStorageKey(storageKey)
  return legacyValue
}

export function createSupabaseBrowserCookieStorage(storageKey: string): SupabaseStorage {
  return {
    getItem(_key) {
      if (!isBrowser()) {
        return null
      }

      const cookieValue = readChunkedCookieValue(storageKey, document.cookie)
      if (cookieValue !== null) {
        return cookieValue
      }

      return migrateLegacyLocalStorageValue(storageKey)
    },
    setItem(_key, value) {
      if (!isBrowser()) {
        return
      }

      for (const name of collectCookieChunkNames(storageKey, document.cookie)) {
        removeBrowserCookie(name)
      }

      const maxAge = resolveSupabaseSessionMaxAge(value)
      for (const { name, value: cookieValue } of buildChunkedCookieRecords(storageKey, value)) {
        setBrowserCookie(name, cookieValue, maxAge)
      }

      removeLegacyLocalStorageKey(storageKey)
    },
    removeItem(_key) {
      if (!isBrowser()) {
        return
      }

      for (const name of collectCookieChunkNames(storageKey, document.cookie)) {
        removeBrowserCookie(name)
      }

      removeLegacyLocalStorageKey(storageKey)
    },
  }
}
